import { useRef, useState } from 'react';

import { Link, useParams } from 'react-router-dom';
import { TldrawUiDialogCloseButton, TldrawUiDialogBody, TldrawUiDialogTitle } from 'tldraw';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';
import { MyOptionType } from '@/types';

interface NodeCreationModalProps {
  onClose: () => void;
  onSave: (nodeData: { dateText: string; description: string; documentNames: string; docId: string }) => void;
}

export const NodeCreationModal = ({ onClose, onSave }: NodeCreationModalProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<MyOptionType>(null);

  const { data: allDocuments } = useGetDocIds(matterId, subsetId);

  const handleSave = () => {
    if (description.trim()) {
      if (!selectedDoc?.value || !selectedDoc?.label) {
        return;
      }

      const documentNames = selectedDoc.label;
      onSave({
        dateText: date.trim(),
        description: description.trim(),
        documentNames: documentNames,
        docId: selectedDoc.value,
      });
      // Reset form
      setDate('');
      setDescription('');
      setSelectedDoc(null);
    }
  };

  const handleClose = () => {
    setDate('');
    setDescription('');
    setSelectedDoc(null);
    onClose();
  };

  const documentOptions =
    allDocuments?.documents?.filter(Boolean).map((doc) => ({
      label: doc.file.name,
      value: doc.id,
    })) ?? [];

  const modalRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="border-b border-gray-200 px-7 py-2">
        <div className="flex items-center justify-between">
          <TldrawUiDialogTitle>
            <p className="text-base font-semibold text-gray-900">Add New Chronology Event</p>
          </TldrawUiDialogTitle>
          <TldrawUiDialogCloseButton />
        </div>
      </div>

      <TldrawUiDialogBody className="w-[450px]">
        <div className="px-4 py-1" ref={modalRef}>
          <div className="flex flex-col gap-4">
            <Link
              onClick={() =>
                trackCustomEvent(TrackingEventsV2.AddFactToChronologyOnVisualChronologyPage, {
                  caseId: matterId,
                })
              }
              to={`/matters/${matterId}/subsets/${subsetId}/facts`}
            >
              <div className="group flex w-full cursor-pointer items-center justify-between rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-blue-600 transition-all duration-200 hover:border-blue-200 hover:bg-blue-100">
                <p className="text-xs text-blue-500">Add a fact to this chronology from the facts table</p>

                <Icon className="lucide--external-link size-3 cursor-pointer transition-transform duration-200 group-hover:scale-110" />
              </div>
            </Link>

            <div className="flex flex-col gap-2">
              <Label htmlFor="date">Date</Label>
              <Input
                type="text"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="e.g., January 15, 2023"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>
              <select
                value={selectedDoc?.value}
                onChange={(e) => setSelectedDoc({ label: e.target.value, value: e.target.value })}
                className="flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap duration-150 focus:border-gray-600 focus:ring-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-gray-500 [&>span]:line-clamp-1 [&>span]:break-all [&>span]:text-ellipsis"
              >
                {documentOptions.map((doc) => (
                  <option key={doc.value} value={doc.value}>
                    {doc.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter event description..."
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-3">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!date.trim() || !selectedDoc?.value || !description.trim()}
            >
              Create Event
            </Button>
          </div>
        </div>
      </TldrawUiDialogBody>
    </>
  );
};
